"use strict";
import { getFewTokenFromOriginalToken, isFewToken } from "@ring-protocol/few-v2-sdk";
import { Token } from "@uniswap/sdk-core";
import { useQueryClient } from "appGraphql/data/apollo/client";
import gql from "graphql-tag";
import { useEffect, useMemo, useState } from "react";
import { COMMON_BASES } from "uniswap/src/constants/routing";
import { Chain } from "uniswap/src/data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { toGraphQLChain } from "uniswap/src/features/chains/utils";
import { useSwapFormContext } from "uniswap/src/features/transactions/swap/contexts/SwapFormContext";
import { currencyToAsset } from "uniswap/src/features/transactions/swap/utils/asset";
import { WrapType } from "uniswap/src/features/transactions/types/wrap";
import { CurrencyField } from "uniswap/src/types/currency";
import { areAddressesEqual } from "uniswap/src/utils/addresses";
const TOKEN_WITH_ORIGIN_QUERY = gql`
  query TokenWithOrigin($id: String!) {
    token(id: $id) {
      id
      chain
      address
      decimals
      name
      symbol
      standard
      originToken {
        address
        name
        symbol
        decimals
        standard
      }
    }
  }
`;
function findOriginTokenInCommonBases(fewToken, chainId) {
  const commonBases = COMMON_BASES[chainId];
  if (!commonBases) {
    return void 0;
  }
  for (const base of commonBases) {
    if (!base.currency.isToken) {
      continue;
    }
    const baseToken = base.currency;
    if (isFewToken(baseToken)) {
      continue;
    }
    try {
      const baseFewToken = getFewTokenFromOriginalToken(baseToken, chainId);
      if (areAddressesEqual(baseFewToken.address, fewToken.address)) {
        return baseToken;
      }
    } catch {
      continue;
    }
  }
  return void 0;
}
async function findOriginTokenViaGraphQL(fewToken, chainId, client) {
  try {
    const graphQLChain = toGraphQLChain(chainId);
    if (!graphQLChain || !client) {
      return void 0;
    }
    const tokenId = `Token:${graphQLChain}_${fewToken.address.toLowerCase()}`;
    const { data } = await client.query({
      query: TOKEN_WITH_ORIGIN_QUERY,
      variables: { id: tokenId },
      fetchPolicy: "cache-first"
    });
    const originTokenData = data?.token?.originToken;
    if (originTokenData?.address && originTokenData?.decimals !== null && originTokenData?.decimals !== void 0) {
      return new Token(
        chainId,
        originTokenData.address,
        originTokenData.decimals,
        originTokenData.symbol ?? void 0,
        originTokenData.name ?? void 0
      );
    }
  } catch {
  }
  return void 0;
}
export function FewTokenOutputAutoUpdater({ enabled }) {
  const { input, output, updateSwapForm, filteredChainIds, derivedSwapInfo } = useSwapFormContext();
  const [loadingOriginToken, setLoadingOriginToken] = useState(false);
  const graphQLChain = useMemo(() => {
    if (input?.chainId) {
      return toGraphQLChain(input.chainId);
    }
    if (output?.chainId) {
      return toGraphQLChain(output.chainId);
    }
    return null;
  }, [input?.chainId, output?.chainId]);
  const ethClient = useQueryClient(Chain.Ethereum);
  const hyperClient = useQueryClient(Chain.Hyper);
  const graphQLClient = useMemo(() => {
    if (!graphQLChain) {
      return null;
    }
    if (graphQLChain === Chain.Ethereum) {
      return ethClient;
    }
    if (graphQLChain === Chain.Hyper) {
      return hyperClient;
    }
    return null;
  }, [graphQLChain, ethClient, hyperClient]);
  useEffect(() => {
    if (!enabled) {
      if (derivedSwapInfo.wrapType === WrapType.FewWrap) {
        if (output) {
          updateSwapForm({ output: void 0 });
        }
      } else if (derivedSwapInfo.wrapType === WrapType.FewUnwrap) {
        if (input) {
          updateSwapForm({ input: void 0 });
        }
      }
      return;
    }
    if (!input && !output) {
      return;
    }
    const inputCurrency = derivedSwapInfo?.currencies[CurrencyField.INPUT]?.currency;
    const outputCurrency = derivedSwapInfo?.currencies[CurrencyField.OUTPUT]?.currency;
    if (!inputCurrency && !outputCurrency) {
      return;
    }
    const focusOnCurrencyField = derivedSwapInfo?.focusOnCurrencyField;
    if (inputCurrency && focusOnCurrencyField === CurrencyField.INPUT) {
      const inputToken = inputCurrency.isNative ? inputCurrency.wrapped : inputCurrency;
      if (!isFewToken(inputToken) && input) {
        let fewToken;
        try {
          fewToken = getFewTokenFromOriginalToken(inputToken, input.chainId);
        } catch {
          return;
        }
        const fewTokenAsset = currencyToAsset(fewToken);
        const isOutputExpected = !!output && fewTokenAsset && output.chainId === fewTokenAsset.chainId && areAddressesEqual(output.address, fewTokenAsset.address);
        if (!isOutputExpected && fewTokenAsset) {
          updateSwapForm({
            output: fewTokenAsset,
            filteredChainIds: {
              ...filteredChainIds,
              [CurrencyField.INPUT]: input.chainId,
              [CurrencyField.OUTPUT]: fewTokenAsset.chainId
            }
          });
        }
      } else if (isFewToken(inputToken) && input && !loadingOriginToken) {
        let originToken = findOriginTokenInCommonBases(inputToken, input.chainId);
        if (!originToken && graphQLClient) {
          setLoadingOriginToken(true);
          findOriginTokenViaGraphQL(inputToken, input.chainId, graphQLClient).then((graphQLOriginToken) => {
            originToken = graphQLOriginToken;
            if (originToken) {
              const originTokenAsset = currencyToAsset(originToken);
              const isOutputExpected = !!output && originTokenAsset && output.chainId === originTokenAsset.chainId && areAddressesEqual(output.address, originTokenAsset.address);
              if (!isOutputExpected && originTokenAsset) {
                updateSwapForm({
                  output: originTokenAsset,
                  filteredChainIds: {
                    ...filteredChainIds,
                    [CurrencyField.INPUT]: input.chainId,
                    [CurrencyField.OUTPUT]: originTokenAsset.chainId
                  }
                });
              }
            }
          }).catch(() => {
          }).finally(() => {
            setLoadingOriginToken(false);
          });
        } else if (originToken) {
          const originTokenAsset = currencyToAsset(originToken);
          const isOutputExpected = !!output && originTokenAsset && output.chainId === originTokenAsset.chainId && areAddressesEqual(output.address, originTokenAsset.address);
          if (!isOutputExpected && originTokenAsset) {
            updateSwapForm({
              output: originTokenAsset,
              filteredChainIds: {
                ...filteredChainIds,
                [CurrencyField.INPUT]: input.chainId,
                [CurrencyField.OUTPUT]: originTokenAsset.chainId
              }
            });
          }
        }
      }
    } else if (outputCurrency && focusOnCurrencyField === CurrencyField.OUTPUT) {
      const outputToken = outputCurrency.isNative ? outputCurrency.wrapped : outputCurrency;
      if (!isFewToken(outputToken) && output) {
        let fewToken;
        try {
          fewToken = getFewTokenFromOriginalToken(outputToken, output.chainId);
        } catch {
          return;
        }
        const fewTokenAsset = currencyToAsset(fewToken);
        const isInputExpected = !!input && fewTokenAsset && input.chainId === fewTokenAsset.chainId && areAddressesEqual(input.address, fewTokenAsset.address);
        if (!isInputExpected && fewTokenAsset) {
          updateSwapForm({
            input: fewTokenAsset,
            filteredChainIds: {
              ...filteredChainIds,
              [CurrencyField.INPUT]: fewTokenAsset.chainId,
              [CurrencyField.OUTPUT]: output.chainId
            }
          });
        }
      } else if (isFewToken(outputToken) && output && !loadingOriginToken) {
        let originToken = findOriginTokenInCommonBases(outputToken, output.chainId);
        if (!originToken && graphQLClient) {
          setLoadingOriginToken(true);
          findOriginTokenViaGraphQL(outputToken, output.chainId, graphQLClient).then((graphQLOriginToken) => {
            originToken = graphQLOriginToken;
            if (originToken) {
              const originTokenAsset = currencyToAsset(originToken);
              const isInputExpected = !!input && originTokenAsset && input.chainId === originTokenAsset.chainId && areAddressesEqual(input.address, originTokenAsset.address);
              if (!isInputExpected && originTokenAsset) {
                updateSwapForm({
                  input: originTokenAsset,
                  filteredChainIds: {
                    ...filteredChainIds,
                    [CurrencyField.INPUT]: originTokenAsset.chainId,
                    [CurrencyField.OUTPUT]: output.chainId
                  }
                });
              }
            }
          }).catch(() => {
          }).finally(() => {
            setLoadingOriginToken(false);
          });
        } else if (originToken) {
          const originTokenAsset = currencyToAsset(originToken);
          const isInputExpected = !!input && originTokenAsset && input.chainId === originTokenAsset.chainId && areAddressesEqual(input.address, originTokenAsset.address);
          if (!isInputExpected && originTokenAsset) {
            updateSwapForm({
              input: originTokenAsset,
              filteredChainIds: {
                ...filteredChainIds,
                [CurrencyField.INPUT]: originTokenAsset.chainId,
                [CurrencyField.OUTPUT]: output.chainId
              }
            });
          }
        }
      }
    }
  }, [enabled, input, output, updateSwapForm, filteredChainIds, derivedSwapInfo, loadingOriginToken, graphQLClient]);
  return null;
}
